import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  untracked,
} from '@angular/core';
import { CommonModule, NgTemplateOutlet } from '@angular/common';
import { Button } from 'primeng/button';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { CollapsibleSectionComponent } from '@brightnav/ui';
import { environment } from '@brighthub/environment';
import { FinancialSnapshotStore } from '../financial-snapshot.store';
import type { MockSnapshotSeed } from '../mock/mock-financial-snapshot-api.service';
import { FinancialSnapshotStateBadgeComponent } from './state-badge.component';
import { FinancialSnapshotMonthlySummaryComponent } from '../summary/monthly-summary.component';
import { FinancialSnapshotSpendingMixComponent } from '../summary/spending-mix.component';
import { FinancialSnapshotProgressComponent } from '../progress/snapshot-progress.component';
import { FinancialSnapshotSkeletonComponent } from './financial-snapshot-skeleton.component';
import {
  FinancialSnapshotEditDialogComponent,
  type FinancialSnapshotEditDialogData,
} from '../edit/financial-snapshot-edit-dialog.component';
import { FinancialSnapshotAnalytics } from '../analytics/financial-snapshot-analytics';
import { FinancialSnapshotSendAction } from '../send/send-to-client.action';

/**
 * Cashflow & Financial Snapshot — 4 states from API `status`.
 * Chrome via shared `bn-collapsible-section`.
 */
@Component({
  selector: 'bn-financial-snapshot-card',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    NgTemplateOutlet,
    Button,
    CollapsibleSectionComponent,
    FinancialSnapshotStateBadgeComponent,
    FinancialSnapshotMonthlySummaryComponent,
    FinancialSnapshotSpendingMixComponent,
    FinancialSnapshotProgressComponent,
    FinancialSnapshotSkeletonComponent,
  ],
  providers: [FinancialSnapshotStore, DialogService],
  templateUrl: './financial-snapshot-card.component.html',
  styleUrl: './financial-snapshot-card.component.scss',
})
export class FinancialSnapshotCardComponent {
  readonly store = inject(FinancialSnapshotStore);
  private readonly _dialog = inject(DialogService);
  private readonly _analytics = inject(FinancialSnapshotAnalytics);
  private readonly _send = inject(FinancialSnapshotSendAction);

  private _lastViewedKey: string | null = null;
  private _outdatedLoggedFor: string | null = null;
  private _editRef: DynamicDialogRef | null = null;
  private _editGuid: string | null = null;

  /** Dev-only fixture switcher (mock API). Hidden in production builds. */
  readonly showDevSwitcher = !environment.production && this.store.mockAvailable;

  readonly devSeeds: { id: MockSnapshotSeed; label: string }[] = [
    { id: 'empty', label: 'Empty' },
    { id: 'in_progress', label: 'In Progress' },
    { id: 'complete', label: 'Complete' },
    { id: 'outdated', label: 'Outdated' },
  ];

  readonly badgeUpdatedAt = computed(() => {
    const snap = this.store.snapshot();
    return snap?.lastUpdatedAt ?? snap?.submittedDate ?? snap?.modifiedDate ?? null;
  });

  constructor() {
    effect(() => {
      const guid = this.store.sessionGuid();
      const status = this.store.status();
      const loading = this.store.loading();
      if (!guid || !status || loading) {
        return;
      }
      const key = `${guid}:${status}`;
      untracked(() => {
        if (this._lastViewedKey !== key) {
          this._lastViewedKey = key;
          this._analytics.viewed(guid, status);
        }
        if (status === 'outdated' && this._outdatedLoggedFor !== guid) {
          this._outdatedLoggedFor = guid;
          this._analytics.outdatedShown(guid);
        }
      });
    });

    // Close edit if the FA switches clients mid-dialog (avoids stale-save UX + wrong-card render).
    effect(() => {
      const guid = this.store.sessionGuid();
      const editGuid = this._editGuid;
      if (editGuid && guid !== editGuid) {
        untracked(() => this._closeEditDialog());
      }
    });
  }

  seedDev(seed: MockSnapshotSeed): void {
    void this.store.seedDevState(seed);
  }

  openEdit(): void {
    const guid = this.store.sessionGuid();
    const template = this.store.template();
    if (!guid || !template) {
      return;
    }
    this._closeEditDialog();
    this._analytics.editOpened(guid);
    const data: FinancialSnapshotEditDialogData = {
      guid,
      snapshot: this.store.snapshot(),
      template,
      store: this.store,
    };
    const ref = this._dialog.open(FinancialSnapshotEditDialogComponent, {
      showHeader: false,
      width: 'min(72rem, 96vw)',
      modal: true,
      closable: false,
      dismissableMask: false,
      closeOnEscape: false,
      contentStyle: {
        overflow: 'hidden',
        padding: '0',
        maxHeight: 'min(85vh, 50rem)',
      },
      styleClass: 'bn-fs-edit-dialog',
      data,
    });
    if (!ref) {
      return;
    }
    this._editRef = ref;
    this._editGuid = guid;
    ref.onClose.subscribe(() => {
      if (this._editRef === ref) {
        this._editRef = null;
        this._editGuid = null;
      }
    });
  }

  sendToClient(): void {
    const guid = this.store.sessionGuid();
    if (!guid) {
      return;
    }
    void this._send.send({
      clientId: guid,
      status: this.store.status(),
      enteredRowCount: this.store.enteredRowCount(),
    });
  }

  private _closeEditDialog(): void {
    const ref = this._editRef;
    if (!ref) {
      this._editGuid = null;
      return;
    }
    this._editRef = null;
    this._editGuid = null;
    ref.close({ cancelled: true, sessionChanged: true });
  }
}
